import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Loader2, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { propertyPeopleAdminAPI } from '../../../lib/api';

// Entry code + messaging for ONE unit, for building admins and super admins
// (the endpoints themselves are already restricted to those roles).
export default function UnitAccessCard({ unitId }) {
    const { t } = useTranslation();
    const [cfg, setCfg] = useState(null);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');

    const load = useCallback(async () => {
        setCfg(null); setCode(''); setError(''); setSaved('');
        try { const r = await propertyPeopleAdminAPI.unitTapEntry(unitId); setCfg(r.data); }
        catch { setCfg({ unavailable: true }); }
    }, [unitId]);

    useEffect(() => { load(); }, [load]);

    const save = async (body, flash) => {
        setBusy(true); setError(''); setSaved('');
        try {
            const r = await propertyPeopleAdminAPI.saveUnitTapEntry(unitId, body);
            setCfg(r.data); setSaved(flash); setCode('');
            setTimeout(() => setSaved(''), 4000);
        } catch (e) {
            setError(e?.response?.status === 400 ? t('propertyPeople.codeRejected') : t('propertyPeople.error'));
        } finally { setBusy(false); }
    };

    if (!cfg) return <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-cyan-400" /></div>;
    if (cfg.unavailable) return <p className="text-sm text-slate-500" data-testid="ua-unavailable">{t('propertyPeople.error')}</p>;

    const pill = (on, onClick, label, testid) => (
        <Button size="sm" variant="outline" onClick={onClick} disabled={busy} data-testid={testid}
            className={`h-7 px-2.5 text-xs ${on ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300'
                : 'border-slate-300 dark:border-slate-600 bg-transparent text-slate-500 dark:text-slate-400'}`}>
            {label}: {on ? t('propertyPeople.on') : t('propertyPeople.off')}
        </Button>
    );

    return (
        <div className="mb-4 rounded-xl border border-cyan-500/20 bg-white dark:bg-slate-900/60 p-3" data-testid="ua-card">
            <div className="mb-1 flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-cyan-300" />
                <p className="font-medium text-slate-900 dark:text-slate-100">{t('propertyPeople.accessTitle')}</p>
            </div>
            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">{t('propertyPeople.accessSubtitle')}</p>

            <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${cfg.code_set
                    ? 'border-emerald-400/30 bg-emerald-500/15 text-emerald-300'
                    : 'border-amber-400/30 bg-amber-500/15 text-amber-300'}`} data-testid="ua-code-status">
                    {cfg.code_set ? t('propertyPeople.codeSet') : t('propertyPeople.codeNotSet')}
                </span>
                {pill(cfg.enabled, () => save({ enabled: !cfg.enabled }, t('propertyPeople.savedFlash')),
                    t('propertyPeople.entryActive'), 'ua-toggle-enabled')}
                {pill(cfg.message_enabled, () => save({ message_enabled: !cfg.message_enabled }, t('propertyPeople.savedFlash')),
                    t('propertyPeople.messagingOn'), 'ua-toggle-messaging')}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    inputMode="numeric" maxLength={4} placeholder={t('propertyPeople.entryCodePlaceholder')}
                    data-testid="ua-code-input"
                    className="w-32 bg-slate-800 border-slate-300 dark:border-slate-600 tracking-[0.3em] text-slate-900 dark:text-slate-100 placeholder:tracking-normal placeholder:text-slate-500" />
                <Button size="sm" onClick={() => save({ entry_code: code }, t('propertyPeople.codeSavedFlash'))}
                    disabled={busy || code.length !== 4} data-testid="ua-code-save"
                    className="bg-gradient-to-r from-cyan-600 to-cyan-500 text-white shadow-lg shadow-cyan-500/20 hover:from-cyan-500 hover:to-cyan-400">
                    {busy ? t('propertyPeople.saving') : (cfg.code_set ? t('propertyPeople.changeCode') : t('propertyPeople.setCode'))}
                </Button>
                {saved && (
                    <span className="flex items-center gap-1 text-xs text-emerald-400" data-testid="ua-saved">
                        <Check className="h-3.5 w-3.5" />{saved}
                    </span>
                )}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{t('propertyPeople.codeHint')}</p>
            {error && <p className="mt-2 text-sm text-rose-400" data-testid="ua-error">{error}</p>}
        </div>
    );
}
